/**
 * Twitter/X Pixel Provider
 *
 * Implements Twitter/X advertising pixel for conversion tracking.
 * Ported from deepsource.com composables/adTracking/providers/twitter.ts
 */

import type { AdTrackingProvider } from '~/types/tracking'

declare global {
  interface Window {
    twq?: (command: string, ...args: unknown[]) => void
  }
}

const isDev = process.env.NODE_ENV === 'development'

interface TwitterPixelConfig {
  pixelId: string
}

export const createTwitterProvider = (config: TwitterPixelConfig): AdTrackingProvider => {
  let isLoaded = false
  const { pixelId } = config

  const loadScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window not available'))
        return
      }

      if (window.twq) {
        resolve()
        return
      }

      window.twq = function (...args: unknown[]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const twqFn = window.twq as any
        if (twqFn.exe) {
          twqFn.exe.apply(window.twq, args)
        } else {
          twqFn.queue.push(args)
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window.twq as any).version = '1.1'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window.twq as any).queue = []

      const script = document.createElement('script')
      script.async = true
      script.src = 'https://static.ads-twitter.com/uwt.js'

      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Twitter pixel'))

      document.head.appendChild(script)
    })
  }

  return {
    name: 'twitter',

    async init() {
      if (!pixelId) {
        if (isDev) {
          console.log('Twitter Pixel: disabled (no pixel ID configured)')
        }
        return
      }

      try {
        await loadScript()

        if (window.twq) {
          window.twq('config', pixelId)
          isLoaded = true

          if (isDev) {
            console.log('Twitter pixel initialized:', pixelId)
          }
        }
      } catch (error) {
        if (isDev) {
          console.warn('Failed to initialize Twitter pixel:', error)
        }
      }
    },

    trackPageView(url?: string) {
      if (!isLoaded || !window.twq) return

      if (url) {
        window.twq('track', 'PageView', { page_location: url })
      }
    },

    trackEvent(eventName: string, params?: Record<string, unknown>) {
      if (!isLoaded || !window.twq) return

      window.twq('track', eventName, params)
    },

    trackConversion(conversionType: string, params?: Record<string, unknown>) {
      if (!isLoaded || !window.twq) return

      const eventMap: Record<string, string> = {
        SignUp: 'tw-signup',
        CompleteRegistration: 'tw-signup',
        Lead: 'tw-lead',
        Purchase: 'tw-purchase',
        AddToCart: 'tw-add-to-cart',
        InitiateCheckout: 'tw-checkout-initiated',
        Subscribe: 'tw-subscribe',
        StartTrial: 'tw-start-trial'
      }

      const twitterEvent = eventMap[conversionType] || conversionType

      window.twq('track', twitterEvent, params)

      if (isDev) {
        console.log('Twitter conversion tracked:', twitterEvent, params)
      }
    },

    isAvailable() {
      return Boolean(pixelId && isLoaded)
    },

    cleanup() {
      isLoaded = false
    }
  }
}
